// AppThemeProvider.jsx
// Wraps the whole app in the brand-matched MUI theme (see appTheme.js) so every
// MUI component renders on-brand and in the right light/dark mode automatically.
//
// The app toggles dark mode + brand by writing `data-layout-mode` / brand CSS
// vars onto <html> (and <body> on the non-auth screens) via the layout saga.
// We watch those with a MutationObserver and rebuild the theme when they change,
// so the theme always mirrors the live Bootstrap tokens — no Redux coupling.
//
// Deliberately NO <CssBaseline>: the app is Bootstrap-first and CssBaseline would
// reset those global styles. We only layer the MUI palette/shape on top.

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@mui/material/styles";
import { buildAppTheme } from "./appTheme";

// Attributes/props whose change means the brand or light/dark mode moved and the
// theme should be rebuilt.
const WATCHED_ATTRS = ["data-layout-mode", "data-layout", "style", "class"];

export default function AppThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => buildAppTheme());

  useEffect(() => {
    let frame = 0;
    const rebuild = () => {
      // Coalesce bursts of attribute writes into a single rebuild next frame.
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setTheme(buildAppTheme());
      });
    };

    // Brand vars are applied asynchronously (Branding component / saga), so
    // rebuild once shortly after mount to pick up the resolved tokens.
    const t = window.setTimeout(rebuild, 0);

    const observer = new MutationObserver(rebuild);
    const opts = { attributes: true, attributeFilter: WATCHED_ATTRS };
    observer.observe(document.documentElement, opts);
    if (document.body) observer.observe(document.body, opts);

    return () => {
      observer.disconnect();
      window.clearTimeout(t);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

AppThemeProvider.propTypes = {
  children: PropTypes.node,
};
